/**
 * Tenant-settings panel for the Organization Brain. Composes the
 * enable/consent settings card with the admin Q&A ("Ask your workspace")
 * panel and resolves the active tenant from context.
 *
 * Mounted from the tenant Settings navigation (`/settings/tenant/:id/org-brain`).
 * The enable/consent flow and the Professional entitlement are enforced
 * server-side — the ai-engine returns 402 for non-entitled tenants — so this
 * panel only resolves the tenant id and supplies it to the feature
 * components. The Ask panel is shown only once the graph is enabled; asking a
 * disabled brain returns nothing useful. React Query dedupes the config fetch
 * shared with OrgBrainSettings.
 */

import { useTenantContextStore } from '@stores/tenantContextStore';
import OrgBrainSettings from './OrgBrainSettings';
import OrgBrainAsk from './OrgBrainAsk';
import OrgBrainSimulate from './OrgBrainSimulate';
import OrgBrainHealth from './OrgBrainHealth';
import OrgBrainInsights from './OrgBrainInsights';
import OrgBrainApiKeys from './OrgBrainApiKeys';
import { useOrgBrainConfig } from './hooks';

export default function OrgBrainSettingsPanel() {
  const { currentTenant } = useTenantContextStore();
  const { data: config } = useOrgBrainConfig(currentTenant?.id);

  if (!currentTenant?.id) {
    return null;
  }

  const enabled = Boolean(config?.is_enabled);

  return (
    <div className="space-y-6">
      <OrgBrainSettings tenantId={currentTenant.id} />
      {enabled && (
        <>
          <OrgBrainAsk tenantId={currentTenant.id} />
          <OrgBrainSimulate tenantId={currentTenant.id} />
          <OrgBrainHealth tenantId={currentTenant.id} />
          <OrgBrainInsights tenantId={currentTenant.id} />
          <OrgBrainApiKeys tenantId={currentTenant.id} />
        </>
      )}
    </div>
  );
}